import { useState } from 'react'
import { notasVentaApi } from '../api/notasVenta'
import Modal from './Modal'
import Boton from './Boton'
import { fmtFecha, fmtHoraDe, fmtMonto } from '../utils/fecha'
import { colors, font, text, radius } from '../design/tokens'

const PASARELAS = [
  { id: 'mobbex', nombre: 'Mobbex', ejemplo: '#JKVWCL2DZTKYKNZGTF7FN5' },
  { id: 'mercadopago', nombre: 'Mercado Pago', ejemplo: '181187083075' },
]

/**
 * Traer a la NV un cobro hecho por FUERA de LGR (Martin, 4 oct 2026): el link
 * se armó en el panel de Mobbex o Mercado Pago y el cliente ya pagó. El
 * vendedor pega el número de operación del comprobante; LGR muestra lo que dice
 * la pasarela (nada se tipea) y, si está bien, lo registra confirmado.
 */
export default function TraerCobroPasarelaModal({ nv, onClose, onRegistrado }) {
  const [gateway, setGateway] = useState('mobbex')
  const [ref, setRef] = useState('')
  const [vista, setVista] = useState(null)
  const [trabajando, setTrabajando] = useState(false)
  const [error, setError] = useState('')

  const mensaje = (e, porDefecto) => {
    const errs = e.response?.data?.errors
    return errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || porDefecto)
  }

  const consultar = async (e) => {
    e?.preventDefault()
    if (!ref.trim()) return
    setTrabajando(true); setError(''); setVista(null)
    try {
      const { data } = await notasVentaApi.consultarCobroPasarela(nv.id, gateway, ref.trim())
      setVista(data)
    } catch (err) {
      setError(mensaje(err, 'No se pudo consultar la operación.'))
    } finally { setTrabajando(false) }
  }

  const registrar = async () => {
    setTrabajando(true); setError('')
    try {
      await notasVentaApi.registrarCobroPasarela(nv.id, gateway, vista.ref)
      onRegistrado()
    } catch (err) {
      setError(mensaje(err, 'No se pudo registrar el cobro.'))
      setTrabajando(false)
    }
  }

  const pasarela = PASARELAS.find(p => p.id === gateway)
  const bloqueado = vista && (vista.ya_registrado || !vista.aprobado)

  return (
    <Modal title="Traer un cobro hecho por fuera de LGR" onClose={onClose} maxWidth="560px">
      <p style={st.nota}>
        Para un link armado en el panel de Mobbex o Mercado Pago que el cliente ya pagó. Pegá el número de
        operación del comprobante: LGR lo consulta en la pasarela y te muestra lo que dice ella.
      </p>

      <form onSubmit={consultar}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {PASARELAS.map(p => (
            <button key={p.id} type="button" onClick={() => { setGateway(p.id); setVista(null); setError('') }}
              style={{ ...st.pill, ...(gateway === p.id ? st.pillOn : {}) }}>{p.nombre}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={ref} onChange={e => { setRef(e.target.value); setVista(null) }} placeholder={`Nº de operación — ej. ${pasarela.ejemplo}`}
            style={st.input} autoFocus />
          <Boton variante="secondary" type="submit" disabled={trabajando || !ref.trim()}>{trabajando && !vista ? 'Consultando…' : 'Consultar'}</Boton>
        </div>
      </form>

      {error && <div style={st.error}>{error}</div>}

      {vista && (
        <div style={st.caja}>
          <div style={st.fila}><span style={st.label}>Estado en {vista.pasarela}</span>
            <strong style={{ color: vista.aprobado ? colors.ok : colors.err }}>{vista.aprobado ? 'Aprobado' : (vista.estado || '—')}</strong></div>
          <div style={st.fila}><span style={st.label}>Importe</span><strong style={st.mono}>$ {fmtMonto(vista.importe)}</strong></div>
          {vista.fecha && <div style={st.fila}><span style={st.label}>Fecha</span><span style={st.mono}>{fmtFecha(vista.fecha)} {fmtHoraDe(vista.fecha)}</span></div>}
          {vista.tarjeta && (vista.tarjeta.marca || vista.tarjeta.last4) && (
            <div style={st.fila}><span style={st.label}>Medio</span>
              <span>{[vista.tarjeta.marca, vista.tarjeta.last4 ? `terminada en ${vista.tarjeta.last4}` : null, vista.tarjeta.cuotas > 1 ? `${vista.tarjeta.cuotas} cuotas` : null].filter(Boolean).join(' · ')}</span></div>
          )}
          <div style={st.fila}><span style={st.label}>Pagó</span>
            <span>{[vista.pagador?.nombre, vista.pagador?.documento ? `DNI/CUIT ${vista.pagador.documento}` : null, vista.pagador?.email].filter(Boolean).join(' · ') || 'La pasarela no informa quién pagó'}</span></div>
          <div style={st.fila}><span style={st.label}>Cliente de la NV</span>
            <span>{[vista.cliente?.nombre, vista.cliente?.documento, vista.cliente?.email].filter(Boolean).join(' · ')}</span></div>

          {vista.ya_registrado && (
            <div style={st.alerta}>Esta operación ya está registrada en LGR{vista.ya_registrado.nvs?.length ? ` (en ${vista.ya_registrado.nvs.join(', ')})` : ''}: no se carga dos veces.</div>
          )}
          {!vista.ya_registrado && vista.coincide_cliente === 'no' && (
            <div style={st.alerta}>⚠ El que pagó NO coincide con el cliente de esta NV (ni documento ni email). Fijate que sea el cobro correcto antes de registrarlo.</div>
          )}
          {!vista.ya_registrado && vista.aprobado && vista.de_mas > 0.009 && (
            <div style={st.aviso}>A esta NV le faltan $ {fmtMonto(Math.max(0, vista.falta_cobrar))}: con este cobro quedarían $ {fmtMonto(vista.de_mas)} cobrados de más.</div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
        {vista && !bloqueado && (
          <Boton variante="primary" onClick={registrar} disabled={trabajando}>{trabajando ? 'Registrando…' : `Registrar $ ${fmtMonto(vista.importe)} en esta NV`}</Boton>
        )}
      </div>
    </Modal>
  )
}

const st = {
  nota: { fontSize: text.sec, color: colors.textSec, marginTop: 0 },
  pill: { padding: '6px 14px', borderRadius: 999, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.textSec, fontSize: text.sec, fontWeight: 600, cursor: 'pointer', fontFamily: font.sans },
  pillOn: { background: colors.text, color: colors.bg, borderColor: colors.text },
  input: { flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: text.body, fontFamily: font.mono },
  error: { marginTop: 10, fontSize: text.sec, color: colors.err },
  caja: { marginTop: 14, padding: 12, borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: colors.bgSoft, display: 'flex', flexDirection: 'column', gap: 6 },
  fila: { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: text.body, color: colors.text, flexWrap: 'wrap' },
  label: { color: colors.textSec, fontSize: text.sec },
  mono: { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' },
  alerta: { marginTop: 6, padding: '8px 10px', borderRadius: radius.sm, background: colors.errBg, color: colors.err, fontSize: text.sec, fontWeight: 600 },
  aviso: { marginTop: 6, padding: '8px 10px', borderRadius: radius.sm, background: colors.warnBg, color: colors.warn, fontSize: text.sec },
}
